// Homophone Harbor: boats carry words that sound the same. Tap the boat that fits the
// sentence and it sails into the harbor.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { makeKeyboard } from '@games/common/keyboard';
import { makeHarborItem, type HarborItem } from '@content/words/homophones';

const ITEMS = 8;

interface Boat { word: string; x: number; y: number; tx: number; ty: number; color: string; sailed: boolean; wobble: number }

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let level = ctx.currentLevel();
  const used = new Set<string>();
  let it: HarborItem = makeHarborItem(level, ctx.rng, used);
  let boats: Boat[] = [];
  let gap = 0;                     // which gap we are filling (for two-gap items)
  let typed = '';
  let attempts = 0, correct = 0, minLevel = level;
  let wrongs = 0, hinted = false, locked = false;
  let t = 0;
  const t0 = performance.now();
  let tProblem = performance.now();
  const COLORS = ['#e2574c', '#4f8fe6', '#5fbf6a', '#f08a3c', '#9b6be6', '#3bb3a9'];

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Boat ', h('strong', null, '1'), ` of ${ITEMS}`), h('button.btn.small.ghost', { onclick: () => ctx.speak(it.speakText) }, 'Say it'));
  const sentence = h('p.center', { style: { fontWeight: '700', fontSize: '21px', minHeight: '56px', padding: '2px 4px' } });
  const stage = h('div');
  const controls = h('div.stack', { style: { gap: '8px' } });
  const why = h('p.center.muted.small', { style: { minHeight: '22px' } });
  host.append(head, sentence, stage, controls, why);
  const S = makeSurface(stage, { aspect: 0.5, draw: false });

  function setup() {
    level = ctx.currentLevel(); minLevel = Math.min(minLevel, level);
    it = makeHarborItem(level, ctx.rng, used);
    gap = 0; typed = ''; wrongs = 0; hinted = false; locked = false;
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    why.textContent = '';
    const W = S.w || 380;
    boats = it.choices.map((w, i) => ({ word: w, x: W * (0.15 + 0.7 * (i / Math.max(1, it.choices.length - 1))) + (it.choices.length === 1 ? W * 0.35 : 0), y: 0, tx: 0, ty: 0, color: COLORS[i % COLORS.length], sailed: false, wobble: i * 1.3 }));
    renderSentence();
    renderControls();
  }

  function renderSentence() {
    let text = it.text;
    // fill answered gaps
    for (let g = 0; g < gap; g++) text = text.replace('___', `[${it.answers[g]}]`);
    sentence.textContent = text;
  }

  function renderControls() {
    clear(controls);
    if (it.kind === 'type') {
      const disp = h('div.prompt', { style: { minHeight: '40px', letterSpacing: '2px' } }, typed || '· · ·');
      const kb = makeKeyboard({ onKey: k => { if (typed.length < 14) { typed += k; disp.textContent = typed; } }, onBackspace: () => { typed = typed.slice(0, -1); disp.textContent = typed || '· · ·'; }, onEnter: () => answer(typed), enterLabel: 'Go' });
      controls.append(h('p.muted.small.center', null, `Type the word that fits. The boats show your choices: ${it.choices.join(', ')}.`), disp, kb.el);
      if (locked) kb.setEnabled(false);
      return;
    }
    controls.append(h('p.muted.small.center', null, it.kind === 'two' ? `Gap ${gap + 1} of 2: tap the boat for the ${gap === 0 ? 'first' : 'second'} blank.` : 'Tap the boat whose word fits the sentence.'));
    controls.append(h('div', { style: { display: 'grid', gridTemplateColumns: `repeat(${Math.min(3, it.choices.length)}, 1fr)`, gap: '8px' } }, ...it.choices.map((w, i) => h('button.choice', { disabled: locked || boats[i]?.sailed, style: { fontSize: '20px', borderColor: boats[i]?.color }, onclick: () => answer(w) }, w))));
  }

  function answer(v: string) {
    if (locked) return;
    const want = it.answers[gap];
    const ok = v.trim().toLowerCase() === want.toLowerCase();
    const ms = performance.now() - tProblem;
    if (ok) {
      const b = boats.find(x => x.word.toLowerCase() === want.toLowerCase());
      if (b) b.sailed = true;
      ctx.sound('ding');
      gap++;
      if (gap >= it.answers.length) {
        locked = true; attempts++; correct++;
        ctx.report({ skill: level >= 3 && level <= 8 ? 'grammar' : 'word_play', level, correct: true, hintUsed: hinted, ms, problemId: `homo:${it.answers.join('+')}` });
        why.textContent = it.why;
        renderSentence(); renderControls();
        ctx.say('Right! The boat sails in.');
        next();
      } else { renderSentence(); renderControls(); ctx.say('One down. Now the next blank.'); }
    } else {
      wrongs++;
      ctx.sound('buzz');
      sentence.classList.remove('shake'); void sentence.offsetWidth; sentence.classList.add('shake');
      const chosen = it.choices.find(c => c.toLowerCase() === v.trim().toLowerCase());
      ctx.say(chosen ? `Not "${chosen}" here. Think about what the sentence means.` : `"${v}" is not one of the boats. Check the spelling.`);
      if (wrongs >= 2) {
        locked = true; attempts++;
        ctx.report({ skill: 'word_play', level, correct: false, hintUsed: hinted, ms, problemId: `homo:${it.answers.join('+')}` });
        gap = it.answers.length;
        for (const a of it.answers) { const b = boats.find(x => x.word.toLowerCase() === a.toLowerCase()); if (b) b.sailed = true; }
        why.textContent = `It was "${it.answers.join('" and "')}". ${it.why}`;
        renderSentence(); renderControls();
        next(1900);
      }
      typed = '';
    }
  }

  function next(delay = 1300) {
    n++;
    if (n >= ITEMS) {
      if (attempts === correct && minLevel >= 5) ctx.charm('charm.harbor');
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), delay);
      return;
    }
    setTimeout(setup, delay);
  }

  function update(dt: number) {
    t += dt;
    const W = S.w, H = S.h;
    boats.forEach((b, i) => {
      const slots = boats.length;
      const homeX = W * (slots === 1 ? 0.5 : 0.14 + 0.72 * (i / (slots - 1)));
      b.tx = b.sailed ? W * 0.5 + (i - (slots - 1) / 2) * 34 : homeX;
      b.ty = b.sailed ? H * 0.78 : H * 0.42;
      b.x += (b.tx - b.x) * Math.min(1, dt * 3);
      b.y += (b.ty - b.y) * Math.min(1, dt * 3);
    });
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    c.fillStyle = '#bfe3f5'; c.fillRect(0, 0, W, H * 0.3);
    c.fillStyle = '#7fc8e8'; c.fillRect(0, H * 0.3, W, H * 0.7);
    // harbor wall
    c.fillStyle = '#9a9aa3'; c.fillRect(0, H * 0.66, W * 0.2, 10); c.fillRect(W * 0.8, H * 0.66, W * 0.2, 10);
    c.fillStyle = '#2a2a2e'; c.font = '800 12px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'alphabetic';
    c.fillText('HARBOR', W / 2, H * 0.98);
    // waves
    c.strokeStyle = 'rgba(255,255,255,.5)'; c.lineWidth = 2;
    for (let i = 0; i < 5; i++) { const y = H * (0.36 + i * 0.13); c.beginPath(); for (let x = 0; x <= W; x += 8) c.lineTo(x, y + Math.sin((x + t * 60 + i * 40) / 25) * 2); c.stroke(); }
    boats.forEach((b, i) => {
      const bob = Math.sin(t * 2 + b.wobble) * 2;
      const x = b.x, y = b.y + bob;
      const w = Math.min(96, W / (boats.length + 0.5)), hh = 18;
      c.fillStyle = b.color; c.beginPath(); c.moveTo(x - w / 2, y); c.lineTo(x + w / 2, y); c.lineTo(x + w / 2 - 10, y + hh); c.lineTo(x - w / 2 + 10, y + hh); c.closePath(); c.fill();
      c.fillStyle = '#f6f3ec'; c.fillRect(x - 2, y - 30, 4, 30);
      c.beginPath(); c.moveTo(x + 2, y - 30); c.lineTo(x + 2 + w * 0.35, y - 8); c.lineTo(x + 2, y - 6); c.closePath(); c.fill();
      c.fillStyle = '#2a2a2e'; c.font = `800 ${b.word.length > 7 ? 12 : 14}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText(b.word, x, y + hh / 2 + 1);
      void i;
    });
  }

  const loop = makeLoop(update, draw);
  loop.start();
  setup();
  return { destroy() { loop.stop(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'words.homophone-harbor',
  title: 'Homophone Harbor',
  blurb: 'Boats carry words that sound alike. Pick the one the sentence needs.',
  category: 'words',
  skills: ['word_play', 'grammar'],
  levelRange: [1, 10],
  unlock: { stars: 12 },
  mount,
};
export default mod;
